/**
 * Centralized language configuration for Reedle.
 * Single source of truth for UI languages, AI response languages, and related utilities.
 */

// --- UI Languages (interface: buttons, menus, navigation) ---

export const UI_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
];

export const UI_LANGUAGE_CODES = UI_LANGUAGES.map(l => l.code);

export const UI_LANGUAGE_NAMES = {
  en: 'English',
  zh: '简体中文',
  ru: 'Русский',
  fr: 'Français',
  es: 'Español',
  ja: '日本語',
  pt: 'Português',
  de: 'Deutsch',
};

// --- AI Response Languages (18 languages the LLM can respond in) ---

export const RESPONSE_LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English', nativeName: 'English' },
  { code: 'zh-Hans', label: '简', name: 'Simplified Chinese', nativeName: '简体中文' },
  { code: 'zh-Hant', label: '繁', name: 'Traditional Chinese', nativeName: '繁體中文' },
  { code: 'ja', label: '日', name: 'Japanese', nativeName: '日本語' },
  { code: 'ru', label: 'RU', name: 'Russian', nativeName: 'Русский' },
  { code: 'fr', label: 'FR', name: 'French', nativeName: 'Français' },
  { code: 'es', label: 'ES', name: 'Spanish', nativeName: 'Español' },
  { code: 'pt', label: 'PT', name: 'Portuguese', nativeName: 'Português' },
  { code: 'ko', label: '한', name: 'Korean', nativeName: '한국어' },
  { code: 'de', label: 'DE', name: 'German', nativeName: 'Deutsch' },
  { code: 'ar', label: 'ع', name: 'Arabic', nativeName: 'العربية' },
  { code: 'hi', label: 'हि', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'vi', label: 'VI', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { code: 'tr', label: 'TR', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'id', label: 'ID', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { code: 'uk', label: 'UK', name: 'Ukrainian', nativeName: 'Українська' },
  { code: 'fi', label: 'FI', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'et', label: 'ET', name: 'Estonian', nativeName: 'Eesti' },
];

// --- Mapping: UI language code → AI language code ---
// UI uses 'zh' (i18n locale), AI uses 'zh-Hans' (BCP 47 script tag)

export const UI_TO_AI_LANG_MAP = {
  en: 'en', zh: 'zh-Hans', ru: 'ru',
  fr: 'fr', es: 'es', ja: 'ja', pt: 'pt', de: 'de',
};

// --- Utilities ---

/**
 * Migrate legacy language codes (e.g. 'zh' → 'zh-Hans').
 * Returns the code unchanged if no migration needed.
 */
export const migrateLegacyLangCode = (code) =>
  code === 'zh' ? 'zh-Hans' : code;

/**
 * Detect browser language and map to the closest RESPONSE_LANGUAGES code.
 * Handles zh-CN → zh-Hans, zh-TW/zh-HK → zh-Hant, etc.
 * Falls back to the provided defaultCode.
 */
export const detectBrowserAILanguage = (defaultCode = 'en') => {
  try {
    const raw = (navigator.language || '').toLowerCase();
    const base = raw.split('-')[0];
    if (base === 'zh') {
      return (raw.includes('hant') || raw.includes('tw') || raw.includes('hk') || raw.includes('mo'))
        ? 'zh-Hant' : 'zh-Hans';
    }
    const match = RESPONSE_LANGUAGES.find(l => l.code === base);
    return match ? match.code : defaultCode;
  } catch {
    return defaultCode;
  }
};
